import { loadJsonFromUrl } from "../util/json-loader";

export interface GalleryImage {
  primary_image: string;
  secondary_images: string[];
  thumb?: string;
  contributor: string | string[];
  description?: string;
  wood_species?: string[];
  finish?: string;
  dimensions?: string;
  category?: string;
  month: string;
  tags?: string[];
}

interface GalleryEntry {
  year: string;
  month?: string;
  name?: string;
}

interface MemberEntry {
  name: string;
  gallery: string;
  count: number;
}

interface GalleriesList {
  show_and_tell: GalleryEntry[];
  competition: GalleryEntry[];
  member: MemberEntry[];
  tag: GalleryEntry[];
}

const galleriesAssetsBaseUrl =
  "https://assets.richmondwoodturners.org/galleries/";

const correctionsFormUrl =
  "https://docs.google.com/forms/d/e/1FAIpQLSf5qv4YvVO_8bKJAKHe6g3uhT3skVxcH9r2-5jTGZRYaLYZ7A/viewform?usp=pp_url&entry.1017505232={primary_image}&entry.1882499680={contributor}&entry.548182191={description}&entry.889294581={wood_species}&entry.448976697={finish}";

let galleriesCache: GalleryImage[] | null = null;
let galleriesListCache: GalleriesList | null = null;
const galleryMonth: Record<string, GalleryImage[]> = {};

export const possibleMonths: Record<string, string> = {
  "01": "January",
  "02": "February",
  "03": "March",
  "04": "April",
  "05": "May",
  "06": "June",
  "07": "July",
  "08": "August",
  "09": "September",
  "10": "October",
  "11": "November",
  "12": "December",
};

async function loadGalleriesImageList(): Promise<GalleryImage[]> {
  if (galleriesCache) {
    return galleriesCache;
  }

  const images = await loadJsonFromUrl<GalleryImage[]>(
    `${galleriesAssetsBaseUrl}images.json`,
  );
  galleriesCache = fixupThumbnailRefs(images ?? []);
  return galleriesCache;
}

async function loadGalleriesList(): Promise<GalleriesList> {
  if (galleriesListCache) {
    return galleriesListCache;
  }

  const galleriesList = await loadJsonFromUrl<GalleriesList>(
    `${galleriesAssetsBaseUrl}galleries.json`,
  );
  galleriesListCache = galleriesList ?? {
    show_and_tell: [],
    competition: [],
    member: [],
    tag: [],
  };
  return galleriesListCache;
}

async function loadGalleryImageList(jsonFile: string): Promise<GalleryImage[]> {
  if (galleryMonth[jsonFile]) {
    return galleryMonth[jsonFile];
  }

  const images = await loadJsonFromUrl<GalleryImage[]>(
    `${galleriesAssetsBaseUrl}${jsonFile}`,
  );
  galleryMonth[jsonFile] = fixupThumbnailRefs(images ?? []);
  return galleryMonth[jsonFile];
}

async function loadGalleryMemberImageList(
  name: string,
): Promise<GalleryImage[]> {
  const nameSlug = name.toLowerCase().split(" ").join("-");
  if (galleryMonth[nameSlug]) {
    return galleryMonth[nameSlug];
  }

  const images = await loadJsonFromUrl<GalleryImage[]>(
    `${galleriesAssetsBaseUrl}${nameSlug}-gallery.json`,
  );
  galleryMonth[nameSlug] = fixupThumbnailRefs(images ?? []);
  return galleryMonth[nameSlug];
}

async function loadGalleryMonthImageList(
  year: string,
  month: string,
): Promise<GalleryImage[]> {
  const cacheKey = `${year}-${month}`;
  if (galleryMonth[cacheKey]) {
    return galleryMonth[cacheKey];
  }

  const images = await loadJsonFromUrl<GalleryImage[]>(
    `${galleriesAssetsBaseUrl}${year}-${month}-gallery.json`,
  );
  galleryMonth[cacheKey] = fixupThumbnailRefs(images ?? []);
  return galleryMonth[cacheKey];
}

function fixupThumbnailRefs(imageList: GalleryImage[]): GalleryImage[] {
  return imageList.map((image) => {
    if (!image.thumb) {
      image.thumb = image.primary_image.replace(".jpg", "-thumb.jpg");
    }
    return image;
  });
}

export async function getGalleryImages(
  type: string,
  year: string | null,
  month: string | null,
  name: string | null,
): Promise<GalleryImage[]> {
  return await loadGalleriesList().then((galleriesList) => {
    let galleryJsonFile = getGalleryJsonFileUrl(
      galleriesList,
      type,
      year,
      month,
      name,
    );

    if (!galleryJsonFile) {
      const latestGallery = getLatestShowAndTellGallery(galleriesList);
      if (latestGallery) {
        return getGalleryImages(
          "show_and_tell",
          latestGallery.year,
          latestGallery.month!!,
          null,
        );
      }
      return [];
    }

    return loadGalleryImageList(galleryJsonFile);
  });
}

function getImagesForMonth(
  imageList: GalleryImage[],
  year: string,
  month: string,
): GalleryImage[] {
  const monthKey = `${year}-${month}`;
  return imageList.filter((image) => image.month === monthKey);
}

function getLatestShowAndTellGallery(
  galleriesList: GalleriesList,
): GalleryEntry | null {
  if (galleriesList.show_and_tell.length === 0) {
    return null;
  }

  const sortedGalleries = galleriesList.show_and_tell.sort((a, b) => {
    const dateA = new Date(parseInt(a.year), parseInt(a.month || "0") - 1);
    const dateB = new Date(parseInt(b.year), parseInt(b.month || "0") - 1);
    return dateB.getTime() - dateA.getTime(); // Sort in descending order
  });

  return sortedGalleries[0];
}

function getLatestImageMonth(imageList: GalleryImage[]): GalleryImage | null {
  if (imageList.length === 0) {
    return null;
  }

  const sortedImages = imageList
    .filter((image) => possibleMonths.hasOwnProperty(image.month.split("-")[1]))
    .sort(
      (a, b) => b.month.localeCompare(a.month), // Sort in descending order
    );

  return sortedImages[0];
}

function getShowAndTellYearsFromGalleryList(
  galleriesList: GalleriesList,
): string[] {
  const years = new Set<string>();
  galleriesList.show_and_tell.forEach((show_and_tell) => {
    years.add(show_and_tell.year);
  });
  return Array.from(years);
}

function selectImages(
  imageList: GalleryImage[],
  type: string,
  year: string,
  month: string,
  name: string,
): GalleryImage[] | null {
  if (type === "show_and_tell") {
    return imageList.filter(
      (image) =>
        (image.category === "Show and Tell" ||
          image.category === "Challenge") &&
        image.month === `${year}-${month}`,
    );
  } else if (type === "competitions") {
    return imageList.filter(
      (image) =>
        image.category === "Competition" &&
        image.month === `${year}-competition`,
    );
  } else if (type === "members") {
    return imageList
      .filter((image) => {
        if (Array.isArray(image.contributor)) {
          return image.contributor.some(
            (contributor) =>
              contributor.toLowerCase().split(" ").join("-") === name,
          );
        } else {
          return image.contributor.toLowerCase().split(" ").join("-") === name;
        }
      })
      .sort((a, b) => {
        return b.month.localeCompare(a.month);
      });
  } else if (type === "tags") {
    return imageList.filter(
      (image) =>
        image.tags &&
        image.tags.some((tag) => tag.toLowerCase() === name.toLowerCase()),
    );
  }

  return null;
}

function buildCompetitionGalleryList(galleryList: GalleriesList): string {
  const pages = galleryList.competition
    .map((competition) => competition.name)
    .sort()
    .map((year) => {
      return buildCompetitionGalleryPageHtml(year || "");
    })
    .join("");

  return `<h2>Turning Competition Gallery</h2>
        <div class="gallery-sections">
            <h3>Competition Year</h3>
            <div class="gallery-section">
                ${pages}
            </div>
        </div>`;
}

function buildCompetitionGalleryPageHtml(year: string): string {
  return `<div class="gallery-page">
        <a href="/galleries?type=competitions&year=${year}">${year}</a>
    </div>`;
}

function buildMemberDirectory(
  galleryList: GalleriesList,
): Record<string, MemberEntry[]> {
  // Build a set of all member names from the gallery list.
  const memberList = new Set<MemberEntry>();
  galleryList.member
    .filter((member) => member.count > 0)
    .forEach((member) => {
      memberList.add(member);
    });

  // Build a map of member names where the key is the first initial of the last name.

  const memberDirectory: Record<string, MemberEntry[]> = {};

  memberList.forEach((member) => {
    const lastName = member.name.split(" ").slice(-1)[0];
    const initial = lastName.charAt(0).toUpperCase();

    if (!memberDirectory[initial]) {
      memberDirectory[initial] = [];
    }

    memberDirectory[initial].push(member);
  });

  // Sort each array by last name, then by first name
  Object.keys(memberDirectory).forEach((initial) => {
    memberDirectory[initial] = memberDirectory[initial].sort((a, b) => {
      const lastNameA = a.name.split(" ").slice(-1)[0];
      const lastNameB = b.name.split(" ").slice(-1)[0];
      const lastNameComparison = lastNameA.localeCompare(lastNameB);

      if (lastNameComparison !== 0) {
        return lastNameComparison;
      }

      // If last names match, sort by first name
      const firstNameA = a.name.split(" ")[0];
      const firstNameB = b.name.split(" ")[0];
      return firstNameA.localeCompare(firstNameB);
    });
  });

  return memberDirectory;
}

function buildMembersGalleryList(galleryList: GalleriesList): string {
  const memberDirectory = buildMemberDirectory(galleryList);

  const sections = Object.keys(memberDirectory)
    .sort()
    .map((initial) => {
      const members = memberDirectory[initial];
      const memberLinks = members
        .map((member) => {
          const nameSlug = member.name.toLowerCase().split(" ").join("-");

          return `<div class="gallery-page">
                        <a href="/galleries?type=members&name=${nameSlug}">${member.name}</a> (${member.count})
                    </div>`;
        })
        .join("");

      return `
                <h3>${initial}</h3>
                <div class="gallery-member-section">
                    ${memberLinks}
                </div>`;
    })
    .join("");

  return `<h2>Member Gallery</h2>
        <div class="gallery-sections">
            ${sections}
        </div>`;
}

function buildShowAndTellGalleryList(galleryList: GalleriesList): string {
  const galleryYears = getShowAndTellYearsFromGalleryList(galleryList);
  const sections = galleryYears
    .sort()
    .reverse()
    .map((year) => {
      return buildShowAndTellGalleryListSectionHtml(galleryList, year);
    })
    .join("");

  return `<h2>Show and Tell Gallery</h2>
        <section class="gallery-sections">
            ${sections}
        </section>`;
}

function buildShowAndTellGalleryListSectionHtml(
  galleryList: GalleriesList,
  year: string,
): string {
  const latestGallery = getLatestShowAndTellGallery(galleryList);
  if (!latestGallery) {
    return ``;
  }

  if (parseInt(year) > parseInt(latestGallery.year)) {
    return ``;
  }

  const monthLinks = Object.keys(possibleMonths)
    .sort()
    .map((month) => {
      if (
        parseInt(month) > parseInt(latestGallery.month || "0") &&
        parseInt(year) === parseInt(latestGallery.year)
      ) {
        return ``;
      }

      if (
        galleryList.show_and_tell.some(
          (gallery) => gallery.year === year && gallery.month === month,
        )
      ) {
        return `<a href="/galleries?type=show_and_tell&year=${year}&month=${month}">${possibleMonths[month]}</a>`;
      } else {
        return `<span class="disabled-gallery-page">${possibleMonths[month]}</span>`;
      }
    })
    .map((link) => `<div class="gallery-page">${link}</div>`)
    .join("");

  return `
        <h3>${year}</h3>
        <div class="gallery-section">
            ${monthLinks}
        </div>`;
}

export function buildCorrectionsFormUrl(image: GalleryImage): string {
  const contributor = Array.isArray(image.contributor)
    ? image.contributor.join(", ")
    : image.contributor || "";
  const woodSpecies =
    image.wood_species && image.wood_species.length > 0
      ? image.wood_species.join(", ")
      : "";

  return correctionsFormUrl
    .replace("{primary_image}", encodeURIComponent(image.primary_image || ""))
    .replace("{contributor}", encodeURIComponent(contributor))
    .replace("{description}", encodeURIComponent(image.description || ""))
    .replace("{wood_species}", encodeURIComponent(woodSpecies))
    .replace("{finish}", encodeURIComponent(image.finish || ""));
}

function buildGallerySlidesHtml(imageList: GalleryImage[]): string {
  return imageList
    .map((image) => {
      const contributor = Array.isArray(image.contributor)
        ? image.contributor.join(", ")
        : image.contributor || "";
      const description = image.description || "";
      const woodSpecies =
        image.wood_species && image.wood_species.length > 0
          ? image.wood_species.join(", ")
          : "";
      const finish = image.finish || "";
      const dimensions = image.dimensions || "";

      // Build caption parts
      let textParts: string[] = [];
      let htmlParts: string[] = [];
      if (contributor) {
        textParts.push(safeText(contributor));
        htmlParts.push(contributorLinkText(image.contributor));
      }
      if (description) {
        textParts.push(safeText(description));
        htmlParts.push(safeText(description));
      }
      if (woodSpecies) {
        textParts.push(`Wood species: ${safeText(woodSpecies)}`);
        htmlParts.push(
          `<strong>Wood species:</strong> ${safeText(woodSpecies)}`,
        );
      }
      if (finish) {
        textParts.push(`Finish: ${safeText(finish)}`);
        htmlParts.push(`<strong>Finish:</strong> ${safeText(finish)}`);
      }
      if (dimensions) {
        textParts.push(`Size: ${safeText(dimensions)}`);
        htmlParts.push(`<strong>Size:</strong> ${safeText(dimensions)}`);
      }

      const captionText = textParts.length > 0 ? textParts.join("\n") : "";

      const captionHtml = htmlParts.length > 0 ? htmlParts.join("<br>") : "";

      const correctionsLink = buildCorrectionsFormUrl(image);

      const correctableHtml = `
        <div class="correctable-caption">
            <div class="caption">${captionHtml}</div>
            <div class="correction-link">
                <a href="${correctionsLink}" target="_blank" rel="noopener noreferrer">
                    <small>Offer corrections</small>
                </a>
            </div>
        </div>
        `;

      return `
        <div class="slideframe">
            <a class="highslide" rel="highslide" href="${galleriesAssetsBaseUrl}${
              image.primary_image
            }">
                <img src="${galleriesAssetsBaseUrl}${
                  image.thumb
                }" alt="${captionText}" title="${captionText}" data-caption="${safeText(
                  correctableHtml,
                )}" />
            </a>
        </div>`;
    })
    .join("");
}

function contributorLinkText(contributor: string | string[]): string {
  if (contributor === "Unknown") {
    return "Unknown";
  }

  if (Array.isArray(contributor)) {
    return contributor
      .map(
        (name) =>
          `<a href="/galleries?type=members&name=${name
            .toLowerCase()
            .split(" ")
            .join("-")}">${safeText(name)}</a>`,
      )
      .join(", ");
  } else {
    return `<a href="/galleries?type=members&name=${contributor
      .toLowerCase()
      .split(" ")
      .join("-")}">${safeText(contributor)}</a>`;
  }
}

export function getGalleryJsonFileUrl(
  galleriesList: GalleriesList,
  type: string,
  year: string | null = null,
  month: string | null = null,
  name: string | null = null,
): string | null {
  if (type === "show_and_tell") {
    if (
      galleriesList.show_and_tell.some(
        (gallery) => gallery.year === year && gallery.month === month,
      )
    ) {
      return `${year}-${month}-gallery.json`;
    }
  } else if (type === "competitions") {
    if (galleriesList.competition.some((gallery) => gallery.name === year)) {
      return `${year}-competition-gallery.json`;
    }
  } else if (type === "members") {
    const member = galleriesList.member.find(
      (member) => member.name.toLowerCase().split(" ").join("-") === name,
    );
    if (member) {
      return member.gallery;
    }
  } else if (type === "tags") {
    if (
      galleriesList.tag.some(
        (tag) => tag.name.toLowerCase() === name?.toLowerCase(),
      )
    ) {
      // For tag-based galleries, we will load the full image list and filter client-side
      return `tag-${name}.json`;
    }
  }

  return null;
}

function safeText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function updateGalleryList(selector: string): void {
  loadGalleriesList().then((galleriesList) => {
    const html =
      buildShowAndTellGalleryList(galleriesList) +
      buildCompetitionGalleryList(galleriesList) +
      buildMembersGalleryList(galleriesList);
    const section = document.querySelector<HTMLElement>(selector);
    if (section) {
      section.innerHTML = html;
    }
  });
}

/**
 * Update gallery slides based on the URL query parameters.
 * - type: "show_and_tell" or "competitions" or "members" or "tags"
 * - year: 4-digit year (for show_and_tell and competitions galleries)
 * - month: 2-digit month (for show_and_tell galleries)
 * - name: member name (for members galleries)
 */
export function updateGallerySlides(
  headerSelector: string,
  slidesSelector: string,
  url: string,
): void {
  loadGalleriesList().then((galleriesList) => {
    const urlParts = url.split("?");

    const params = new URLSearchParams(urlParts[1]);
    const type = params.get("type") ?? "show_and_tell";
    const year = params.get("year") ?? "";
    const month = params.get("month") ?? "";
    const name = params.get("name") ?? "";

    let galleryJsonFile = getGalleryJsonFileUrl(
      galleriesList,
      type,
      year,
      month,
      name,
    );

    if (!galleryJsonFile) {
      const latestGallery = getLatestShowAndTellGallery(galleriesList);
      if (latestGallery) {
        const substituteUrl = `${urlParts[0]}?type=show_and_tell&year=${latestGallery.year}&month=${latestGallery.month}`;
        updateGallerySlides(headerSelector, slidesSelector, substituteUrl);
      }
      return;
    }

    loadGalleryImageList(galleryJsonFile).then((imageList) => {
      const galleryImages = selectImages(imageList, type, year, month, name);
      if (!galleryImages) return;

      let headerText = "";
      if (type === "show_and_tell") {
        headerText = `Show and Tell—${possibleMonths[month]} ${year}`;
      } else if (type === "competitions") {
        headerText = `Turning Competition—${year}`;
      } else if (type === "members" || type === "tags") {
        headerText = name
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      }

      const header = document.querySelector<HTMLElement>(headerSelector);
      if (header) {
        header.innerHTML = headerText;
      }

      const html = buildGallerySlidesHtml(galleryImages);
      const section = document.querySelector<HTMLElement>(slidesSelector);
      if (section) {
        section.innerHTML = html;
      }

      // Re-initialize highslide for the new dynamic content
      if (typeof (window as any).hs !== "undefined") {
        (window as any).hs.updateAnchors();
        (window as any).hs.setClickEvents();
      }
    });
  });
}
